import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Bookmark, Trash2, BookOpen, History, Plus, MessageSquare, StickyNote } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userApi } from '../services/api';
import { Button } from '../components/ui/button';
import { SectionHeader, PageLoading, GlassCard, EmptyState } from '../components/shared';
import { toast } from 'sonner';

const CONTENT_TYPE_ICON = {
  quran: BookOpen,
  hadith: MessageSquare,
  historical: History,
};

const contentLink = (type, id, preview) => {
  if (type === 'quran' && preview?.surah_number) return `/quran/${preview.surah_number}`;
  if (type === 'historical') return `/events/${id}`;
  return '#';
};

const PreviewBody = ({ type, preview, contentId }) => {
  if (!preview) {
    return <p className="text-text-muted text-sm">{contentId}</p>;
  }
  if (type === 'quran') {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-primary font-medium">
            {preview.surah_name} · {preview.surah_number}:{preview.verse_number}
          </span>
        </div>
        {preview.arabic_text && (
          <p
            className="font-arabic text-lg text-white text-right mb-2 leading-loose line-clamp-2"
            dir="rtl"
          >
            {preview.arabic_text}
          </p>
        )}
        {preview.translation_french && (
          <p className="text-sm text-text-secondary line-clamp-2 italic">
            « {preview.translation_french} »
          </p>
        )}
      </div>
    );
  }
  if (type === 'historical') {
    return (
      <div>
        <p className="text-text-primary font-medium">{preview.title}</p>
        {preview.date && <p className="text-xs text-text-muted mt-1">{preview.date}</p>}
      </div>
    );
  }
  if (type === 'hadith') {
    return (
      <div>
        {preview.grade && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 mr-2">
            {preview.grade}
          </span>
        )}
        <p className="text-sm text-text-secondary line-clamp-2 mt-1">{preview.translation}</p>
      </div>
    );
  }
  return <p className="text-text-muted text-sm">{contentId}</p>;
};

const Bookmarks = () => {
  const { t } = useTranslation();
  const CONTENT_TYPE_LABEL = {
    quran: t('bookmarks.type_quran'),
    hadith: t('bookmarks.type_hadith'),
    historical: t('bookmarks.type_historical'),
    tafsir: t('bookmarks.type_tafsir'),
    scholarly: t('bookmarks.type_scholarly'),
  };
  const { user, loading: authLoading } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookmarks');

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        const [bookmarksRes, notesRes] = await Promise.all([
          userApi.getBookmarks().catch(() => ({ data: [] })),
          userApi.getNotes().catch(() => ({ data: [] })),
        ]);
        setBookmarks(bookmarksRes.data || []);
        setNotes(notesRes.data || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const handleDeleteBookmark = async (id) => {
    try {
      await userApi.deleteBookmark(id);
      setBookmarks((prev) => prev.filter((b) => b.id !== id));
      toast.success(t('bookmarks.bookmark_deleted'));
    } catch (error) {
      toast.error(t('bookmarks.delete_failed'));
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      await userApi.deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success(t('bookmarks.note_deleted'));
    } catch (error) {
      toast.error(t('bookmarks.delete_note_failed'));
    }
  };

  if (authLoading || loading) return <PageLoading />;
  if (!user) {
    return <Navigate to="/login" state={{ from: { pathname: '/bookmarks' } }} replace />;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <SectionHeader
          overline={t('bookmarks.overline')}
          title={t('bookmarks.title')}
          subtitle={t('bookmarks.subtitle')}
        />

        <div className="flex gap-2 mb-8">
          <Button
            variant={activeTab === 'bookmarks' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bookmarks')}
            className={activeTab === 'bookmarks' ? 'bg-primary text-background' : 'border-border text-text-secondary'}
            data-testid="tab-bookmarks"
          >
            <Bookmark className="w-4 h-4 mr-2" />
            {t('bookmarks.tab_bookmarks')} ({bookmarks.length})
          </Button>
          <Button
            variant={activeTab === 'notes' ? 'default' : 'outline'}
            onClick={() => setActiveTab('notes')}
            className={activeTab === 'notes' ? 'bg-primary text-background' : 'border-border text-text-secondary'}
            data-testid="tab-notes"
          >
            <StickyNote className="w-4 h-4 mr-2" />
            {t('bookmarks.tab_notes')} ({notes.length})
          </Button>
        </div>

        {activeTab === 'bookmarks' && (
          <div className="space-y-4">
            {bookmarks.length === 0 ? (
              <EmptyState
                icon={Bookmark}
                title={t('bookmarks.empty_bookmarks_title')}
                description={t('bookmarks.empty_bookmarks_desc')}
              />
            ) : (
              bookmarks.map((bookmark, index) => {
                const Icon = CONTENT_TYPE_ICON[bookmark.content_type] || Bookmark;
                const to = contentLink(bookmark.content_type, bookmark.content_id, bookmark.content_preview);
                return (
                  <motion.div
                    key={bookmark.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    data-testid={`bookmark-item-${bookmark.id}`}
                  >
                    <GlassCard className="flex items-start justify-between gap-4 hover:border-primary/30 transition-all">
                      <Link to={to} className="flex items-start gap-4 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-primary uppercase tracking-wider">
                            {CONTENT_TYPE_LABEL[bookmark.content_type] || bookmark.content_type}
                          </span>
                          <div className="mt-2">
                            <PreviewBody
                              type={bookmark.content_type}
                              preview={bookmark.content_preview}
                              contentId={bookmark.content_id}
                            />
                          </div>
                          {bookmark.note && (
                            <p className="text-xs text-text-muted mt-2 italic">{bookmark.note}</p>
                          )}
                        </div>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteBookmark(bookmark.id)}
                        className="text-text-muted hover:text-red-400 flex-shrink-0"
                        aria-label={t('bookmarks.delete_bookmark')}
                        data-testid={`bookmark-delete-${bookmark.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </GlassCard>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4">
            {notes.length === 0 ? (
              <EmptyState
                icon={StickyNote}
                title={t('bookmarks.empty_notes_title')}
                description={t('bookmarks.empty_notes_desc')}
              />
            ) : (
              notes.map((note, index) => {
                const Icon = CONTENT_TYPE_ICON[note.content_type] || StickyNote;
                const to = contentLink(note.content_type, note.content_id, note.content_preview);
                return (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    data-testid={`note-item-${note.id}`}
                  >
                    <GlassCard>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-secondary/30 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <Link
                              to={to}
                              className="text-xs text-primary uppercase tracking-wider hover:underline"
                            >
                              {t('bookmarks.note_label')} · {CONTENT_TYPE_LABEL[note.content_type] || note.content_type}
                              {note.content_preview?.surah_number &&
                                ` · ${note.content_preview.surah_number}:${note.content_preview.verse_number}`}
                            </Link>
                            {note.content_preview && (
                              <div className="mt-2 pb-3 mb-3 border-b border-border/40">
                                <PreviewBody
                                  type={note.content_type}
                                  preview={note.content_preview}
                                  contentId={note.content_id}
                                />
                              </div>
                            )}
                            <p className="text-text-primary mt-2 whitespace-pre-wrap">
                              {note.content}
                            </p>
                            <p className="text-xs text-text-muted mt-3">
                              {new Date(note.updated_at || note.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-text-muted hover:text-red-400 flex-shrink-0"
                          aria-label={t('bookmarks.delete_note')}
                          data-testid={`note-delete-${note.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </GlassCard>
                  </motion.div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookmarks;
